import { HttpError } from "@azure/msal-browser/custom-auth";

export type NativeAuthFlowName = "signIn" | "signUp" | "resetPassword";

export type UiErrorAction = "redirect" | "goToReset" | "goToSignIn" | "restart";

export interface UiError {
  /** Plain-language, actionable copy. Never contains raw service error codes or descriptions. */
  message: string;
  field?: "email" | "password" | "code";
  action?: UiErrorAction;
  /** Correlation ID for support, shown in small print. */
  reference?: string;
}

export const PASSWORD_POLICY_MESSAGE =
  "Use 8 to 64 characters with at least three of: uppercase letters, lowercase letters, numbers and symbols.";

interface ErrorWithData {
  errorData?: { correlationId?: string };
}

/** Calls an SDK error type guard (e.g. isInvalidCode) when the error has one. */
function is(error: unknown, guard: string): boolean {
  const candidate = (error as Record<string, unknown> | null | undefined)?.[guard];
  return typeof candidate === "function" && (candidate as () => unknown).call(error) === true;
}

/**
 * Maps a native authentication SDK error to user-facing copy. Unknown users and wrong passwords share
 * one message so the sign-in form cannot be used to discover which emails have accounts.
 */
export function toUiError(
  error: unknown,
  flow: NativeAuthFlowName,
  options: { supportEmail?: string | null } = {}
): UiError {
  const errorData = (error as ErrorWithData | null | undefined)?.errorData;
  const withReference = (uiError: UiError): UiError =>
    errorData?.correlationId ? { ...uiError, reference: errorData.correlationId } : uiError;

  if (is(error, "isRedirectRequired") || is(error, "isUnsupportedChallengeType")) {
    return { message: "", action: "redirect" };
  }

  if (is(error, "isTokenExpired")) {
    return withReference({ message: "Your session timed out. Please start again.", action: "restart" });
  }

  if (is(error, "isPasswordResetRequired")) {
    return withReference({
      message: "You need to reset your password before you can sign in.",
      action: "goToReset",
    });
  }

  if (is(error, "isInvalidCode")) {
    return withReference({
      message: "That code is incorrect or has expired. Check your email or request a new code.",
      field: "code",
    });
  }

  if (
    flow === "signIn" &&
    (is(error, "isUserNotFound") || is(error, "isPasswordIncorrect") || is(error, "isInvalidPassword"))
  ) {
    return withReference({ message: "Email or password is incorrect.", field: "password" });
  }

  if (is(error, "isUserAlreadyExists")) {
    return withReference({
      message: "An account with this email already exists. Sign in instead.",
      field: "email",
      action: "goToSignIn",
    });
  }

  if (is(error, "isInvalidPassword")) {
    return withReference({
      message: `Choose a stronger password. ${PASSWORD_POLICY_MESSAGE}`,
      field: "password",
    });
  }

  if (is(error, "isInvalidUsername")) {
    return withReference({ message: "Enter a valid email address.", field: "email" });
  }

  if (is(error, "isMissingRequiredAttributes") || is(error, "isAttributesValidationFailed")) {
    return withReference({
      message: options.supportEmail
        ? `We couldn't create your account. Contact ${options.supportEmail} for help.`
        : "We couldn't create your account. Please contact support.",
    });
  }

  if (flow === "resetPassword" && is(error, "isUserNotFound")) {
    return withReference({
      message: "We couldn't start a password reset for that email. Check the address and try again.",
      field: "email",
    });
  }

  if (is(error, "isPasswordResetFailed")) {
    return withReference({
      message: "We couldn't reset your password. Please try again.",
      action: "restart",
    });
  }

  if (errorData instanceof HttpError) {
    return withReference({
      message: "We can't reach the sign-in service right now. Check your connection and try again.",
    });
  }

  return withReference({ message: "Something went wrong. Please try again." });
}
